import React from 'react';
import { PrismicImage } from '../../utils/queries/types';
import Helmet from 'react-helmet';

const AmazeeIoMetadata = ({ page_meta_description, page_meta_thumbnail, location, title }: { title: string, page_meta_description?: string, page_meta_thumbnail?: PrismicImage, location: Location }) => {
    const defaultPageMetaDescription = 'Your open source PaaS: A developer-centric application delivery platform that manages infrastructure & operations, so developers don’t have to.';
    const defaultImageUrl = `https://amazee.io/images/amazee-io-home.jpg`;
    const siteUrl = 'https://amazee.io';
    const t = `${title}`;
    const description = page_meta_description || defaultPageMetaDescription;

    const getImageMimeType = (url: string): string => {
        const ext = url.split('.').pop()?.toLowerCase().split('?')[0];
        const mimeTypes: Record<string, string> = {
            'jpg': 'image/jpeg',
            'jpeg': 'image/jpeg',
            'png': 'image/png',
            'webp': 'image/webp',
            'gif': 'image/gif',
        };
        return mimeTypes[ext || ''] || 'image/jpeg';
    };

    const cleanImageUrl = (url: string): string => {
        // Keep the URL as-is but ensure HTTPS - don't strip Prismic query params
        return url.replace('http://', 'https://');
    };

    const rawImageUrl = page_meta_thumbnail?.url
        ? (page_meta_thumbnail.url.startsWith('/')
            ? `https://amazee.io${page_meta_thumbnail.url}`
            : page_meta_thumbnail.url)
        : defaultImageUrl;

    const image = cleanImageUrl(rawImageUrl);
    const imageMimeType = getImageMimeType(rawImageUrl);

    // Build canonical URL from static base + pathname (SSR-safe)
    const canonicalUrl = `${siteUrl}${location?.pathname || ''}`;
    const siteDomain = 'amazee.io';

    return (
        <Helmet>
            <style>
            {`
                * {
                    font-family: 'Heebo', 'Arial', 'arial', sans-serif;
                }
            `}
            </style>

            {/*<!-- Primary Meta Tags -->*/}
            <meta data-rh="true" name="robots" content="index,follow"/>
            <meta name="title" content={t} />
            <meta name="description" content={description} />

            {/*<!-- Open Graph -->*/}
            <meta property="og:type" content="website" />
            <meta content="text/html; charset=UTF-8" name="Content-Type" />
            <meta property="og:url" content={canonicalUrl} />
            <meta property="og:title" content={t} />
            <meta property="og:description" content={description} />
            <meta property="og:image" content={image} />
            <meta property="og:image:src" content={image} />
            <meta property="og:image:secure_url" content={image} />
            <meta property="og:image:type" content={imageMimeType} />
            
            {/*<!-- Twitter tags -->*/}
            <meta property="twitter:card" content="summary_large_image"/>
            <meta property="twitter:url" content={canonicalUrl} />
            <meta property="twitter:domain" content={siteDomain} />
            <meta property="twitter:description" content={description} />
            <meta property="twitter:title" content={t} />
            <meta property="twitter:site" content="@amazeeio"/>
            <meta property="twitter:image" content={image} />
            <meta property="twitter:image:src" content={image} />
        </Helmet>
    )
}

export default AmazeeIoMetadata;
